/**
 * Documents API Service
 * Handles all document-related API operations
 *
 * Base URL: /api/documents
 */
import { useCallback, useMemo } from "react";
import { useApi } from "@/hooks/use-api";
import type {
    CustomFieldDefinitionDto,
    DocumentDetailDto,
    DocumentExportableResponseDto,
    DocumentListParams,
    DocumentListResponseDto,
    DocumentPresenceResponseDto,
    DocumentStatusDto,
    ExportRequestDto,
    ExportResponseDto,
    GetCustomFieldDefinitionsResponseDto,
    GetDocumentStatusBatchRequestDto,
    GetDocumentStatusBatchResponseDto,
    GetDocumentTypesResponseDto,
    GetDocumentsByIdsRequestDto,
    GetDocumentsByIdsResponseDto,
    UpdateCustomFieldsRequestDto,
    UpdateCustomFieldsResponseDto,
    UpdateStatusBatchRequestDto,
    UpdateStatusBatchResponseDto,
} from "@/types/api/documents";
import type { GlSuggestionFeedbackRequestDto, GlSuggestionsResponseDto } from "@/types/api/gl-codes";

// Base URL for documents endpoints (apiFetch adds /api prefix)
const DOCUMENTS_BASE = "/documents";

/**
 * Hook providing document API operations with authentication
 */
export function useDocumentsApi() {
    const api = useApi();

    /**
     * Get all documents with filtering and pagination
     * GET /api/documents
     */
    const getDocuments = useCallback(
        async (params?: DocumentListParams): Promise<DocumentListResponseDto> => {
            const searchParams = new URLSearchParams();

            if (params?.status) searchParams.set("status", params.status);
            if (params?.organizationId) searchParams.set("organizationId", params.organizationId.toString());
            if (params?.createdAfter) searchParams.set("createdAfter", params.createdAfter);
            if (params?.createdBefore) searchParams.set("createdBefore", params.createdBefore);
            if (params?.limit) searchParams.set("limit", params.limit.toString());
            if (params?.offset) searchParams.set("offset", params.offset.toString());

            const query = searchParams.toString();
            const endpoint = query ? `${DOCUMENTS_BASE}?${query}` : DOCUMENTS_BASE;

            return api.get<DocumentListResponseDto>(endpoint);
        },
        [api],
    );

    /**
     * Get a single document by ID with full details
     * GET /api/documents/{id}
     */
    const getDocumentById = useCallback(
        async (id: number): Promise<DocumentDetailDto> => {
            return api.get<DocumentDetailDto>(`${DOCUMENTS_BASE}/${id}`);
        },
        [api],
    );

    /**
     * Get document status (lightweight polling endpoint)
     * GET /api/documents/{id}/status
     */
    const getDocumentStatus = useCallback(
        async (id: number): Promise<DocumentStatusDto> => {
            return api.get<DocumentStatusDto>(`${DOCUMENTS_BASE}/${id}/status`);
        },
        [api],
    );

    /**
     * Get status of multiple documents in batch (lightweight polling endpoint)
     * POST /api/documents/status/batch/get
     * Use this instead of multiple getDocumentStatus calls to reduce API spam
     */
    const getDocumentStatusBatch = useCallback(
        async (documentIds: number[]): Promise<GetDocumentStatusBatchResponseDto> => {
            const dto: GetDocumentStatusBatchRequestDto = { documentIds };
            return api.post<GetDocumentStatusBatchResponseDto>(`${DOCUMENTS_BASE}/status/batch/get`, dto);
        },
        [api],
    );

    /**
     * Get multiple documents by IDs
     * POST /api/documents/batch
     */
    const getDocumentsByIds = useCallback(
        async (documentIds: number[]): Promise<GetDocumentsByIdsResponseDto> => {
            const dto: GetDocumentsByIdsRequestDto = { documentIds };
            return api.post<GetDocumentsByIdsResponseDto>(`${DOCUMENTS_BASE}/batch`, dto);
        },
        [api],
    );

    /**
     * Get available document types for the tenant
     * GET /api/documents/types
     */
    const getDocumentTypes = useCallback(async (): Promise<GetDocumentTypesResponseDto> => {
        return api.get<GetDocumentTypesResponseDto>(`${DOCUMENTS_BASE}/types`);
    }, [api]);

    /**
     * Get custom field definitions for a document type
     * GET /api/documents/types/{documentTypeId}/customfields
     *
     * Pass the document's organizationId so definitions come from the org that owns
     * the document, not the caller's first org (needed for users with multiple orgs).
     */
    const getCustomFieldDefinitions = useCallback(
        async (documentTypeId: number, organizationId?: number | null): Promise<GetCustomFieldDefinitionsResponseDto> => {
            const query = organizationId != null ? `?organizationId=${organizationId}` : "";
            return api.get<GetCustomFieldDefinitionsResponseDto>(`${DOCUMENTS_BASE}/types/${documentTypeId}/customfields${query}`);
        },
        [api],
    );

    /**
     * Export documents (returns data for frontend CSV generation when format is csv,
     * or backend-handled export when format is external)
     * POST /api/documents/export
     * Automatically updates document status to Queued
     */
    const exportDocuments = useCallback(
        async (documentIds: number[], format: "csv" | "external" = "csv"): Promise<ExportResponseDto> => {
            const dto: ExportRequestDto = { documentIds, format };
            return api.post<ExportResponseDto>(`${DOCUMENTS_BASE}/export`, dto);
        },
        [api],
    );

    /**
     * Batch update document statuses
     * POST /api/documents/status/batch
     */
    const batchUpdateStatus = useCallback(
        async (dto: UpdateStatusBatchRequestDto): Promise<UpdateStatusBatchResponseDto> => {
            return api.post<UpdateStatusBatchResponseDto>(`${DOCUMENTS_BASE}/status/batch`, dto);
        },
        [api],
    );

    /**
     * Delete a document (soft delete)
     * DELETE /api/documents/{id}
     * Sets status to Deleted and cancels pending jobs
     */
    const deleteDocument = useCallback(
        async (id: number): Promise<void> => {
            await api.delete<void>(`${DOCUMENTS_BASE}/${id}`);
        },
        [api],
    );

    /**
     * Batch soft-delete multiple documents
     * DELETE /api/documents/batch
     */
    const deleteDocuments = useCallback(
        async (ids: number[]): Promise<void> => {
            const query = ids.map((id) => `ids=${id}`).join("&");
            await api.delete<unknown>(`${DOCUMENTS_BASE}/batch?${query}`);
        },
        [api],
    );

    /**
     * Get document with related documents for export
     * GET /api/documents/{id}/exportable
     * Returns main document, related documents, and relationship details
     */
    const getDocumentExportable = useCallback(
        async (id: number): Promise<DocumentExportableResponseDto> => {
            return api.get<DocumentExportableResponseDto>(`${DOCUMENTS_BASE}/${id}/exportable`);
        },
        [api],
    );

    /**
     * Update custom field values for a document
     * PATCH /api/documents/{id}/customfields
     * Supports add, replace, and delete operations
     */
    const updateCustomFields = useCallback(
        async (id: number, dto: UpdateCustomFieldsRequestDto): Promise<UpdateCustomFieldsResponseDto> => {
            return api.patch<UpdateCustomFieldsResponseDto>(`${DOCUMENTS_BASE}/${id}/customfields`, dto);
        },
        [api],
    );

    /**
     * Update the organization assigned to a document
     * PATCH /api/documents/{id}/organization
     */
    const updateDocumentOrganization = useCallback(
        async (id: number, organizationId: number): Promise<void> => {
            await api.patch(`${DOCUMENTS_BASE}/${id}/organization`, { organizationId });
        },
        [api],
    );

    /**
     * Get only the vendor-name custom field definition for a document type.
     * GET /api/documents/types/{documentTypeId}/vendor-field
     * Used by the vendor-lookup-failure UI to show/edit just the vendor name field.
     */
    const getVendorNameField = useCallback(
        async (documentTypeId: number, organizationId?: number | null): Promise<CustomFieldDefinitionDto> => {
            const query = organizationId != null ? `?organizationId=${organizationId}` : "";
            return api.get<CustomFieldDefinitionDto>(`${DOCUMENTS_BASE}/types/${documentTypeId}/vendor-field${query}`);
        },
        [api],
    );

    /**
     * Re-trigger vendor lookup for a document whose lookup previously failed (BizVendorNotFound),
     * after the user has corrected the visible fields.
     * POST /api/documents/{id}/relookup
     * Backend resolves the prior vendor-not-found exception, requeues the failed VendorLookup job,
     * and returns the document to a processing state (status polling resumes).
     */
    const relookupVendor = useCallback(
        async (id: number): Promise<void> => {
            await api.post<void>(`${DOCUMENTS_BASE}/${id}/relookup`, {});
        },
        [api],
    );

    /**
     * Document presence heartbeat: registers "I am viewing this document" and returns how
     * many OTHER users are currently viewing it. POST /api/documents/{id}/presence
     * Informational only — never locks or blocks anything.
     */
    const heartbeatPresence = useCallback(
        async (id: number): Promise<DocumentPresenceResponseDto> => {
            return api.post<DocumentPresenceResponseDto>(`${DOCUMENTS_BASE}/${id}/presence`, {});
        },
        [api],
    );

    /**
     * Retry a failed external export for a document (latest job is a failed ExternalExport).
     * POST /api/documents/{id}/retry-export
     * Backend creates a new export job mirroring the failed one (preserved for audit); the
     * document returns to the in-flight export state and status polling resumes. 409 when
     * there is no failed export to retry (e.g. a retry is already in flight).
     */
    const retryExport = useCallback(
        async (id: number): Promise<void> => {
            await api.post<void>(`${DOCUMENTS_BASE}/${id}/retry-export`, {});
        },
        [api],
    );

    /**
     * Get (or generate on first call) ranked GL code suggestions per line item.
     * POST /api/documents/{id}/gl-suggestions
     * Server-gated by the FEATURE_GL_SUGGEST entitlement (403 when not enabled).
     * Suggestions are evidence only — nothing is written to the document.
     */
    const generateGlSuggestions = useCallback(
        async (id: number): Promise<GlSuggestionsResponseDto> => {
            return api.post<GlSuggestionsResponseDto>(`${DOCUMENTS_BASE}/${id}/gl-suggestions`, {});
        },
        [api],
    );

    /**
     * Record accept/override telemetry for saved GL values on lines that had suggestions.
     * POST /api/documents/{id}/gl-suggestions/feedback
     * Best-effort — callers should fire and forget; failures never block saving.
     */
    const postGlSuggestionFeedback = useCallback(
        async (id: number, dto: GlSuggestionFeedbackRequestDto): Promise<void> => {
            await api.post<void>(`${DOCUMENTS_BASE}/${id}/gl-suggestions/feedback`, dto);
        },
        [api],
    );

    return useMemo(
        () => ({
            getDocuments,
            getDocumentById,
            getDocumentStatus,
            getDocumentStatusBatch,
            getDocumentsByIds,
            getDocumentTypes,
            getCustomFieldDefinitions,
            getDocumentExportable,
            exportDocuments,
            batchUpdateStatus,
            deleteDocument,
            deleteDocuments,
            updateCustomFields,
            updateDocumentOrganization,
            relookupVendor,
            retryExport,
            getVendorNameField,
            heartbeatPresence,
            generateGlSuggestions,
            postGlSuggestionFeedback,
        }),
        [
            getDocuments,
            getDocumentById,
            getDocumentStatus,
            getDocumentStatusBatch,
            getDocumentsByIds,
            getDocumentTypes,
            getCustomFieldDefinitions,
            getDocumentExportable,
            exportDocuments,
            batchUpdateStatus,
            deleteDocument,
            deleteDocuments,
            updateCustomFields,
            updateDocumentOrganization,
            relookupVendor,
            retryExport,
            getVendorNameField,
            heartbeatPresence,
            generateGlSuggestions,
            postGlSuggestionFeedback,
        ],
    );
}

/**
 * Type for the return value of useDocumentsApi
 */
export type DocumentsApi = ReturnType<typeof useDocumentsApi>;
